import React, { useEffect, useState } from 'react';
import {
  Box, Container, Card, CardBody, VStack, Heading, Text, Button, Select,
  FormControl, FormLabel, Alert, AlertIcon, Spinner, Image, Link,
} from '@chakra-ui/react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import logoVertical from '../assets/logo/aplica-ia-logo-vertical.svg';
import { publicApi } from '../services/api';

// Página pública de cancelar inscrição (LGPD/anti-spam) — acessada pelo rodapé do e-mail.
const Unsubscribe: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [emailMasked, setEmailMasked] = useState('');
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<null | 'out' | 'in'>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    publicApi.unsubscribeVerify(token)
      .then((r) => { setValid(true); setEmailMasked(r.email_masked); if (r.opted_out) setDone('out'); })
      .catch(() => setValid(false))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (resubscribe: boolean) => {
    setSubmitting(true);
    try {
      const r = await publicApi.unsubscribe({ token, reason, resubscribe });
      setDone(r.opted_out ? 'out' : 'in');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box bg="gray.50" minH="100vh" display="flex" alignItems="center">
      <Container maxW="md">
        <Card>
          <CardBody>
            <VStack spacing={5} align="stretch">
              <Image src={logoVertical} alt="Aplica IA" h="52px" objectFit="contain" alignSelf="center" />

              {loading ? (
                <Box textAlign="center" py={6}><Spinner color="brand.500" /></Box>
              ) : !valid ? (
                <Alert status="error" borderRadius="md"><AlertIcon />Link inválido ou expirado.</Alert>
              ) : done === 'out' ? (
                <VStack spacing={3} align="stretch">
                  <Alert status="success" borderRadius="md"><AlertIcon />Inscrição cancelada.</Alert>
                  <Text fontSize="sm" color="gray.600">
                    Você não receberá mais e-mails de novidades e dicas. Avisos importantes da sua
                    conta (segurança, cobrança) continuam sendo enviados.
                  </Text>
                  <Button variant="outline" size="sm" isLoading={submitting} onClick={() => submit(true)}>
                    Mudei de ideia — voltar a receber
                  </Button>
                </VStack>
              ) : done === 'in' ? (
                <Alert status="success" borderRadius="md"><AlertIcon />Pronto! Você voltará a receber nossos e-mails.</Alert>
              ) : (
                <VStack spacing={4} align="stretch">
                  <Heading size="md" textAlign="center" color="brand.700">Cancelar inscrição</Heading>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    Cancelar os e-mails de novidades e dicas para <b>{emailMasked}</b>?
                  </Text>
                  <FormControl>
                    <FormLabel fontSize="sm">Motivo (opcional)</FormLabel>
                    <Select size="sm" placeholder="Prefiro não dizer" value={reason} onChange={(e) => setReason(e.target.value)}>
                      <option value="Recebo e-mails demais">Recebo e-mails demais</option>
                      <option value="Não é relevante para mim">Não é relevante para mim</option>
                      <option value="Não me lembro de ter me cadastrado">Não me lembro de ter me cadastrado</option>
                      <option value="Outro">Outro</option>
                    </Select>
                  </FormControl>
                  <Button colorScheme="brand" onClick={() => submit(false)} isLoading={submitting}>
                    Cancelar inscrição
                  </Button>
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    Você continuará recebendo apenas avisos importantes da conta (segurança e cobrança).
                  </Text>
                </VStack>
              )}

              <Text fontSize="sm" textAlign="center" color="gray.400">
                <Link as={RouterLink} to="/" color="brand.500">Voltar ao site</Link>
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default Unsubscribe;
